import { Controller } from "@hotwired/stimulus"

const POLL_INTERVAL_MS = 4000
// Long enough for a slow render, short enough that we stop hammering the app
// while someone fills in the signup form.
const POLL_TIMEOUT_MS = 240000
// Ceiling on how long one poll request can sit before we give up on it.
const FETCH_TIMEOUT_MS = 15000
// Ceiling on how long settling waits for one shot to come down the wire.
const IMAGE_LOAD_TIMEOUT_MS = 20000

export default class extends Controller {
  static targets = ["card", "status"]
  static values = {
    url: String,
    pending: Boolean,
  }

  connect() {
    this.startedAt = Date.now()
    if (this.pendingValue) {
      this.deadline = setTimeout(() => this.settle(), POLL_TIMEOUT_MS)
      this.scheduleTick()
    }
  }

  disconnect() {
    this.stopPolling()
  }

  scheduleTick() {
    this.timer = setTimeout(() => this.tick(), POLL_INTERVAL_MS)
  }

  stopPolling() {
    if (this.timer) clearTimeout(this.timer)
    this.timer = null
    if (this.deadline) clearTimeout(this.deadline)
    this.deadline = null
  }

  async tick() {
    this.timer = null
    if (Date.now() - this.startedAt > POLL_TIMEOUT_MS) return this.settle()

    try {
      const response = await this.fetchProgress()
      if (!response.ok) return this.settle()

      const data = await response.json()
      // Wait for the shots to finish downloading before settling. A mockup that
      // lands on the same poll that clears `pending` is still loading when
      // settle() runs, and settle() drops every card that is not ready yet.
      await Promise.all((data.images || []).map((image) => this.render(image)))

      if (data.pending) {
        this.scheduleTick()
      } else {
        this.settle()
      }
    } catch (error) {
      this.settle()
    }
  }

  fetchProgress() {
    const abort = typeof AbortController === "undefined" ? null : new AbortController()
    const expiry = setTimeout(() => abort?.abort(), FETCH_TIMEOUT_MS)

    return fetch(this.urlValue, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      signal: abort?.signal,
    }).finally(() => clearTimeout(expiry))
  }

  render(image) {
    if (!image.image_url) return Promise.resolve()

    const card = this.cardTargets.find((el) => el.dataset.scene === image.scene)
    if (!card || card.classList.contains("is-ready")) return Promise.resolve()

    const frame = card.querySelector(".onb-show__frame")
    if (!frame) return Promise.resolve()

    return new Promise((resolve) => {
      const img = document.createElement("img")
      const expiry = setTimeout(resolve, IMAGE_LOAD_TIMEOUT_MS)
      const finish = () => {
        clearTimeout(expiry)
        resolve()
      }

      img.alt = image.alt || ""
      img.addEventListener("load", () => {
        frame.replaceChildren(img)
        card.classList.add("is-ready")
        finish()
      })
      img.addEventListener("error", finish)
      img.src = image.image_url
    })
  }

  // Whatever arrived is what they get: drop the skeletons so a failed shot does
  // not sit there pulsing forever.
  settle() {
    if (this.settled) return

    this.settled = true
    this.stopPolling()
    this.cardTargets.forEach((card) => {
      if (!card.classList.contains("is-ready")) card.remove()
    })
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = this.cardTargets.length
        ? "Your coffee, shot and ready to sell."
        : "Your bag is designed and waiting for you."
    }
  }
}
